import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

// Modal scroll lock. While any modal layer is open the document behind it
// must not move, and only scrollable regions inside the modal may scroll.
//   - `overflow: hidden` on <html> locks desktop and Android. <body> is left
//     alone: with <html> hidden it locks nothing more, it only turns <body>
//     into a clip box. In the installed iOS app <body> is one top safe area
//     shorter than the screen, so clipping the page to it shrank the document
//     and the whole viewport with it (innerHeight, `dvh`, fixed layers) for as
//     long as the modal stayed open.
//   - iOS still pans the document under a touch drag, and chains a scroller's
//     overscroll into it, whatever `overflow` says. A non-passive `touchmove`
//     guard cancels every drag that would not scroll something inside a modal.
//   - Nothing is repositioned (no `position: fixed` body), so the page behind
//     does not jump, sticky headers stay put and no scroll event is faked.
//   - If the document was displaced anyway (the keyboard can pan it), the last
//     unlock puts it back exactly where it was, unless the route has changed.
let bodyLockCount = 0;
let previousRootOverflow = "";
let previousRootOverscroll = "";
let lockedScroll = null;
let touchStart = null;
const modalStack = [];
const OVERLAY_SELECTOR = ".lz-v2-overlay";
const SCROLLABLE_OVERFLOW = new Set(["auto", "scroll", "overlay"]);

function canScroll(element, axis) {
  const style = window.getComputedStyle(element);
  if (axis === "x") {
    return SCROLLABLE_OVERFLOW.has(style.overflowX) && element.scrollWidth > element.clientWidth;
  }
  return SCROLLABLE_OVERFLOW.has(style.overflowY) && element.scrollHeight > element.clientHeight;
}

// The nearest scrollable region between the touch target and its modal
// overlay, or null (a touch outside any modal, or on a part that cannot scroll).
export function findModalScroller(target, axis) {
  const overlay = target?.closest?.(OVERLAY_SELECTOR);
  if (!overlay) return null;
  for (let element = target; element && element !== overlay; element = element.parentElement) {
    if (canScroll(element, axis)) return element;
  }
  return null;
}

// Whether a drag of (deltaX, deltaY) from `target` only scrolls inside a
// modal. Everything else would move the page behind, or chain into it from a
// region already at its edge.
export function isModalScrollGesture(target, deltaX, deltaY) {
  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return Boolean(findModalScroller(target, "x"));
  }
  const scroller = findModalScroller(target, "y");
  if (!scroller) return false;
  const atTop = scroller.scrollTop <= 0;
  const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
  if (deltaY > 0 && atTop) return false;
  if (deltaY < 0 && atBottom) return false;
  return true;
}

function handleTouchStart(event) {
  const touch = event.touches?.length === 1 ? event.touches[0] : null;
  touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
}

function handleTouchMove(event) {
  // Pinch and other multi-touch gestures are not scrolling.
  if (!touchStart || event.touches?.length !== 1 || !event.cancelable) return;
  const touch = event.touches[0];
  const deltaX = touch.clientX - touchStart.x;
  const deltaY = touch.clientY - touchStart.y;
  if (!isModalScrollGesture(event.target, deltaX, deltaY)) event.preventDefault();
}

function currentRoute() {
  return `${window.location.pathname}${window.location.search}`;
}

function lockBodyScroll() {
  if (bodyLockCount === 0) {
    const root = document.documentElement;
    previousRootOverflow = root.style.overflow;
    previousRootOverscroll = root.style.overscrollBehavior;
    lockedScroll = { x: window.scrollX, y: window.scrollY, route: currentRoute() };
    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
  }
  bodyLockCount += 1;
}

function unlockBodyScroll() {
  bodyLockCount = Math.max(0, bodyLockCount - 1);
  if (bodyLockCount === 0) {
    const root = document.documentElement;
    root.style.overflow = previousRootOverflow;
    root.style.overscrollBehavior = previousRootOverscroll;
    document.removeEventListener("touchstart", handleTouchStart);
    document.removeEventListener("touchmove", handleTouchMove);
    touchStart = null;
    const saved = lockedScroll;
    lockedScroll = null;
    if (
      saved &&
      saved.route === currentRoute() &&
      (window.scrollX !== saved.x || window.scrollY !== saved.y)
    ) {
      window.scrollTo(saved.x, saved.y);
    }
  }
}

function getFocusableElements(surface) {
  return Array.from(surface?.querySelectorAll(FOCUSABLE_SELECTOR) || []).filter(
    (element) => element.getAttribute("aria-hidden") !== "true"
  );
}

export default function useModalLayer({
  open,
  onClose,
  surfaceRef,
  initialFocusRef,
}) {
  const onCloseRef = useRef(onClose);
  const restoreFocusRef = useRef(null);
  const layerIdRef = useRef(Symbol("lz-v2-modal-layer"));

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const layerId = layerIdRef.current;
    const surface = surfaceRef.current;
    restoreFocusRef.current = document.activeElement;
    modalStack.push(layerId);
    lockBodyScroll();

    const firstFocusable = initialFocusRef?.current || getFocusableElements(surface)[0];
    (firstFocusable || surface)?.focus({ preventScroll: true });

    function handleKeyDown(event) {
      if (modalStack[modalStack.length - 1] !== layerId) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.("escape");
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = getFocusableElements(surfaceRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        surfaceRef.current?.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const index = modalStack.lastIndexOf(layerId);
      if (index >= 0) {
        modalStack.splice(index, 1);
      }
      unlockBodyScroll();
      const returnTarget = restoreFocusRef.current;
      if (returnTarget?.isConnected) {
        returnTarget.focus({ preventScroll: true });
      }
    };
  }, [initialFocusRef, open, surfaceRef]);
}
